import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";

const LOCALSTORAGE_KEY = "notification_selected_centers";

interface SelectedCentersContextType {
  selectedCenters: string[];
  setSelectedCenters: (centers: string[]) => void;
}

const SelectedCentersContext = createContext<SelectedCentersContextType | null>(null);

function readFromStorage(): string[] {
  try {
    const stored = localStorage.getItem(LOCALSTORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

export function SelectedCentersProvider({ children }: { children: ReactNode }) {
  const [selectedCenters, setSelectedCentersState] = useState<string[]>(readFromStorage);

  const setSelectedCenters = useCallback((centers: string[]) => {
    setSelectedCentersState(centers);
    localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(centers));
  }, []);

  // Écouter les changements depuis d'autres onglets (multi-tab)
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === LOCALSTORAGE_KEY && e.newValue) {
        try {
          setSelectedCentersState(JSON.parse(e.newValue));
        } catch {
          // ignore
        }
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  return (
    <SelectedCentersContext.Provider value={{ selectedCenters, setSelectedCenters }}>
      {children}
    </SelectedCentersContext.Provider>
  );
}

export function useSelectedCenters(): SelectedCentersContextType {
  const ctx = useContext(SelectedCentersContext);
  if (!ctx) {
    throw new Error("useSelectedCenters must be used within a SelectedCentersProvider");
  }
  return ctx;
}
